// components/portal/PropertyRegionFolders.tsx
// Assets page as a folder browser: Région → Type d'actif → the scoped assets table. Region is
// derived from each asset's address (server-side, /api/inventory/properties/geo-buckets); the leaf
// renders the shared PropertiesTable scoped to that region+type bucket's ids.
'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Folder, Building2, Loader2 } from '@/components/ui/lucide-memo';
import { useKernelStore } from '@/kernel/kernelStores';
import { PropertiesTable } from '@/components/entities/tables/properties/PropertiesTable';
import { Breadcrumb, FolderGrid, FolderCard, FolderSearch } from './folderBrowserUI';

interface TypeBucket { type: string; count: number; ids: string[] }
interface RegionBucket { region: string; count: number; typeCount: number; types: TypeBucket[] }

export const PropertyRegionFolders = () => {
  const fr = useKernelStore((s) => s.language) === 'fr';
  const [data, setData] = useState<{ regions: RegionBucket[] } | null>(null);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState('');
  const [region, setRegion] = useState<string | null>(null);
  const [type, setType] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/inventory/properties/geo-buckets')
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((j) => { if (!cancelled) setData(j); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, []);

  const rootLabel = fr ? 'Actifs par région' : 'Assets by region';
  const regionData = useMemo(() => data?.regions.find((r) => r.region === region) ?? null, [data, region]);
  const typeData = useMemo(() => regionData?.types.find((t) => t.type === type) ?? null, [regionData, type]);

  const goToRoot = useCallback(() => { setRegion(null); setType(null); }, []);
  const clearType = useCallback(() => setType(null), []);
  const clearRegion = useCallback(() => setRegion(null), []);

  const trimmedQuery = query.trim();
  // PropertiesTable is React.memo'd: rebuilding these props on every render would re-render the
  // whole scoped table for nothing.
  const searchFilters = useMemo(() => ({ search: trimmedQuery }), [trimmedQuery]);
  const scoped = useMemo(() => {
    // The properties API caps an ids fetch at 500. Passing more than the table can ever fully load
    // makes it retry the "still-missing" ids forever (the infinite-loading bug). Cap the scoped set
    // below that ceiling; flag when a big bucket is truncated.
    const CAP = 480;
    const all = typeData?.ids ?? [];
    return { cap: CAP, ids: all.slice(0, CAP), total: all.length, truncated: all.length > CAP };
  }, [typeData]);

  // Leaf: scoped assets table
  if (region && type && typeData) {
    return (
      <div className="flex h-full w-full flex-col">
        <Breadcrumb items={[
          { label: fr ? 'Actifs' : 'Assets', onClick: goToRoot },
          { label: region, onClick: clearType },
          { label: type },
        ]} />
        <div className="min-h-0 flex-1">
          <PropertiesTable
            key={`${region}-${type}`}
            scopedIds={scoped.ids}
            assetLedgerTitle={`${region} · ${type}${scoped.truncated ? ` · ${fr ? `${scoped.cap} premiers sur ${scoped.total}` : `first ${scoped.cap} of ${scoped.total}`}` : ''}`}
            assetLedgerBreadcrumb={`${fr ? 'Actifs' : 'Assets'} / ${region}`}
          />
        </div>
      </div>
    );
  }

  if (!data && !failed) {
    return (
      <div className="flex h-full w-full items-center justify-center gap-2 text-[13px] text-[#8A8A8A]">
        <Loader2 size={16} className="animate-spin" /> {fr ? 'Chargement des dossiers…' : 'Loading folders…'}
      </div>
    );
  }
  if (failed || !data) {
    return <div className="flex h-full w-full items-center justify-center text-[13px] text-[#8A8A8A]">{fr ? 'Indisponible.' : 'Unavailable.'}</div>;
  }

  // Region selected → its asset-type folders
  if (region && regionData) {
    return (
      <div className="flex h-full w-full flex-col">
        <Breadcrumb items={[{ label: fr ? 'Actifs' : 'Assets', onClick: clearRegion }, { label: region }]} />
        <FolderGrid>
          {regionData.types.map((t) => (
            <FolderCard
              key={t.type}
              icon={<Building2 size={20} className="text-[#7A7A7A] transition-colors group-hover:text-[#9A9A9A]" />}
              title={t.type}
              subtitle={`${t.count.toLocaleString(fr ? 'fr-FR' : 'en-US')} ${fr ? 'actifs' : 'assets'}`}
              onClick={() => setType(t.type)}
            />
          ))}
        </FolderGrid>
      </div>
    );
  }

  // Root → region folders
  return (
    <div className="flex h-full w-full flex-col">
      <Breadcrumb items={[{ label: rootLabel }]} />
      <FolderSearch
        value={query}
        onChange={setQuery}
        placeholder={fr ? 'Rechercher un actif…' : 'Search for an asset…'}
      />
      {trimmedQuery ? (
        <div className="min-h-0 flex-1">
          <PropertiesTable key={`search:${trimmedQuery}`} initialFilters={searchFilters} hideSearch />
        </div>
      ) : (
      <FolderGrid>
        {data.regions.map((r) => (
          <FolderCard
            key={r.region}
            icon={<Folder size={22} className="text-[#7A7A7A] transition-colors group-hover:text-[#9A9A9A]" />}
            title={r.region}
            subtitle={`${r.count.toLocaleString(fr ? 'fr-FR' : 'en-US')} ${fr ? 'actifs' : 'assets'} · ${r.typeCount} ${fr ? 'types' : 'types'}`}
            onClick={() => setRegion(r.region)}
          />
        ))}
      </FolderGrid>
      )}
    </div>
  );
};

PropertyRegionFolders.displayName = 'PropertyRegionFolders';
